/**
 * /accept-invite/:token
 *
 * Public page. An invitee lands here from the link Tim sent them.
 * We look up the invite by token, display the email (read-only),
 * and collect username, display name, phone, password.
 *
 * On success: the user is auto-logged-in (tokens stored) and sent to home.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { api, ApiError, setTokens } from '../api/client.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import './AcceptInvitePage.css';
import { usePageTitle } from '../hooks/usePageTitle.js';
import PhoneInput from '../components/PhoneInput.jsx';


// Mirror of backend validation, for immediate client-side feedback.
const USERNAME_RE = /^[a-z0-9_-]{3,32}$/;
const PHONE_RE = /^\+[1-9]\d{7,14}$/;


export default function AcceptInvitePage() {
  usePageTitle('Accept invite');

  const { token } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  // invite lookup
  const [invite, setInvite] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [inviteError, setInviteError] = useState('');

  // form state
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // submission
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Look up the invite as soon as we have the token.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    (async () => {
      try {
        const data = await api.get(`/api/invites/${encodeURIComponent(token)}`, {
          skipAuth: true,
        });
        if (cancelled) return;
        setInvite(data);
        if (data.prefill_username)     setUsername(data.prefill_username);
        if (data.prefill_display_name) setDisplayName(data.prefill_display_name);
        if (data.prefill_phone_number) setPhoneNumber(data.prefill_phone_number);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setInviteError(err.detail || 'this invite is invalid or has expired');
        } else {
          setInviteError('could not load this invite. check your connection and try again.');
        }
      } finally {
        if (!cancelled) setLoadingInvite(false);
      }
    })();

    return () => { cancelled = true; };
  }, [token]);

  // Live validation for the submit button.
  const usernameNormalized = username.trim().toLowerCase();
  const usernameValid = USERNAME_RE.test(usernameNormalized);
  const displayNameValid = displayName.trim().length >= 1 && displayName.trim().length <= 64;
  const phoneValid = PHONE_RE.test(phoneNumber.trim().replace(/[\s\-().]/g, ''));
  const passwordValid = password.length >= 10;
  const canSubmit = !submitting && usernameValid && displayNameValid && phoneValid && passwordValid;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setSubmitError('');

    try {
      const result = await api.post(
        '/api/invites/accept',
        {
          token,
          username: usernameNormalized,
          display_name: displayName.trim(),
          phone_number: phoneNumber.trim().replace(/[\s\-().]/g, ''),
          password,
        },
        { skipAuth: true },
      );

      // Auto-login: the backend gave us tokens.
      setTokens(result.tokens);
      await refreshUser();
      navigate('/', { replace: true });
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.detail : 'something went wrong');
    } finally {
      setSubmitting(false);
    }
  }

  // ── renders ──────────────────────────────────────

  if (loadingInvite) {
    return (
      <div className="accept-page">
        <div className="accept-card accept-loading">
          <div className="accept-loader">…</div>
          <div>checking your invite</div>
        </div>
      </div>
    );
  }

  if (inviteError || !invite) {
    return (
      <div className="accept-page">
        <div className="accept-card accept-error-card">
          <div className="accept-sticker error">⚠ invite problem</div>
          <h1 className="accept-title">Can't use this link</h1>
          <p className="accept-sub">{inviteError || 'this invite is invalid or has expired'}</p>
          <p className="accept-help">
            text Tim and ask for a fresh one.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="accept-page">
      <main className="accept-stage">
        <section className="accept-card">
          <div className="accept-card-head">
            <span className="accept-sticker">🌮 you've been invited</span>
            <h1 className="accept-title">Let's get you set up</h1>
            <p className="accept-sub">
              a few details to finish creating your account
            </p>
          </div>

          <div className="accept-email-row">
            <div className="accept-email-label">account email</div>
            <div className="accept-email-value">{invite.email}</div>
          </div>

          {submitError && (
            <div className="accept-error" role="alert">
              <span aria-hidden="true">⚠</span>
              <span>{submitError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="accept-field">
              <label className="accept-label" htmlFor="ac-username">username</label>
              <input
                id="ac-username"
                type="text"
                autoComplete="username"
                placeholder="something-short"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
              />
              <div className="accept-help-text">
                3–32 characters, letters/digits/dash/underscore
              </div>
            </div>

            <div className="accept-field">
              <label className="accept-label" htmlFor="ac-display">display name</label>
              <input
                id="ac-display"
                type="text"
                autoComplete="name"
                placeholder="what should we call you?"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
              <div className="accept-help-text">
                shows up on your comments — can be anything
              </div>
            </div>

            <div className="accept-field">
              <label className="accept-label" htmlFor="ac-phone">phone number</label>
              <PhoneInput
                id="ac-phone"
                value={phoneNumber}
                onChange={setPhoneNumber}
              />
            </div>

            <div className="accept-field">
              <label className="accept-label" htmlFor="ac-password">password</label>
              <div className="accept-input-wrap">
                <input
                  id="ac-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="make it a good one"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="accept-pw-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? 'hide' : 'show'}
                </button>
              </div>
              <div className="accept-help-text">at least 10 characters</div>
            </div>

            <button
              type="submit"
              className="accept-submit"
              disabled={!canSubmit}
            >
              {submitting ? 'creating your account…' : 'create account  →'}
            </button>
          </form>
        </section>
      </main>

      <div className="accept-tagline">
        <strong>NachoFiles</strong> — the reason the chicken crossed the road
      </div>
    </div>
  );
}
